import type {
  CloudWorkspaceConnectOutcome,
  CloudWorkspaceEndpoint,
  EnvironmentId,
} from "@afar-research/contracts";
import * as Context from "effect/Context";
import * as Effect from "effect/Effect";

import { cloudWorkspaceConnectionFailure } from "./cloudWorkspaceConnection.logic.ts";
import { ConnectionBlockedError, type ConnectionAttemptError } from "./model.ts";

/**
 * Reaching a Cloud Workspace's Environment over its Cloud Workspace Connection (ADR-0132).
 *
 * The endpoint is the workspace's own hostname rather than a loopback address that only
 * exists while something local is running, so unlike an Environment Forward there is no
 * process to own and no scope to close: what the gateway returns stays reachable on its own.
 */
export interface CloudWorkspaceConnectionRequest {
  readonly environmentId: EnvironmentId;
  readonly label: string;
}

export interface CloudWorkspaceConnectionGatewayService {
  readonly open: (
    request: CloudWorkspaceConnectionRequest,
  ) => Effect.Effect<CloudWorkspaceEndpoint, ConnectionAttemptError>;
}

const openWithoutAccount = Effect.fn("CloudWorkspaceConnectionGateway.openWithoutAccount")(
  function* (request: CloudWorkspaceConnectionRequest) {
    return yield* new ConnectionBlockedError({
      reason: "credential",
      detail: `${request.label} is reached through AFAR Connect, which this client is not signed in to.`,
    });
  },
);

/**
 * Supplied by a client holding an AFAR Product Account. The default refuses, so a surface without
 * an account never appears able to reach a Linked Computer.
 */
export class CloudWorkspaceConnectionGateway extends Context.Reference<CloudWorkspaceConnectionGatewayService>(
  "@afar-research/client-runtime/connection/CloudWorkspaceConnectionGateway",
  { defaultValue: () => ({ open: openWithoutAccount }) },
) {}

/** Adapts the account bridge's Cloud Workspace introduction to the gateway the resolver asks for. */
export const makeCloudWorkspaceConnectionGateway = (
  connectEnvironment: (request: {
    environmentId: string;
    label: string;
  }) => Promise<CloudWorkspaceConnectOutcome>,
): CloudWorkspaceConnectionGatewayService => ({
  open: Effect.fn("CloudWorkspaceConnectionGateway.open")(function* (request) {
    const outcome = yield* Effect.tryPromise({
      try: () => connectEnvironment({ environmentId: request.environmentId, label: request.label }),
      catch: (cause) => cloudWorkspaceConnectionFailure(request.label, cause),
    });
    if (outcome._tag === "CloudWorkspaceRefused") {
      return yield* cloudWorkspaceConnectionFailure(request.label, outcome);
    }
    return outcome.endpoint;
  }),
});
